import type { Node as PMNode } from "@tiptap/pm/model";
import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { createElement } from "react";
import {
  DEFAULT_DOCUMENT_EVIDENCE_LABEL,
  readEvidenceLabelFromAttrs,
} from "../evidence/evidence-runs";
import { documentEvidenceRunsPlugin } from "./plugins/document-evidence-runs";
import { DocumentEvidenceNodeView } from "./views/document-evidence-node-view";

function parseOptionalInteger(value: string | null): number | null {
  if (value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function renderDocumentEvidencePlaintext(attrs: Record<string, unknown>): string {
  return readEvidenceLabelFromAttrs(attrs);
}

export const DocumentEvidenceNode = Node.create({
  name: "documentEvidence",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  // Every attribute declares an explicit `data-*` mapping (or `rendered: false`). Without
  // it Tiptap emits each attribute as a bare HTML attribute and re-parses it through
  // `fromString`, which both leaks internal diff state into the clipboard and coerces a
  // numeric-looking document name (`"2024"`) into a number the server then rejects.
  addAttributes() {
    return {
      label: {
        default: DEFAULT_DOCUMENT_EVIDENCE_LABEL,
        parseHTML: (element) =>
          element.getAttribute("data-evidence-label") ?? element.textContent ?? undefined,
        renderHTML: (attributes) => ({
          "data-evidence-label": readEvidenceLabelFromAttrs(attributes),
        }),
      },
      artifactId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-artifact-id"),
        renderHTML: (attributes) => ({ "data-artifact-id": attributes.artifactId }),
      },
      processingRevisionId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-processing-revision-id"),
        renderHTML: (attributes) => ({
          "data-processing-revision-id": attributes.processingRevisionId,
        }),
      },
      blockId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-block-id"),
        renderHTML: (attributes) => ({ "data-block-id": attributes.blockId }),
      },
      startChar: {
        default: null,
        parseHTML: (element) => parseOptionalInteger(element.getAttribute("data-start-char")),
        renderHTML: (attributes) => ({ "data-start-char": attributes.startChar }),
      },
      endChar: {
        default: null,
        parseHTML: (element) => parseOptionalInteger(element.getAttribute("data-end-char")),
        renderHTML: (attributes) => ({ "data-end-char": attributes.endChar }),
      },
      _diffStatus: { default: null, rendered: false },
      _diffOldValue: { default: null, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="documentEvidence"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "reference-chip reference-chip--evidence",
        "data-type": "documentEvidence",
        "data-reference-kind": "evidence",
        "data-diff-status": node.attrs._diffStatus,
      }),
      readEvidenceLabelFromAttrs(node.attrs),
    ];
  },

  renderText({ node }) {
    return renderDocumentEvidencePlaintext(node.attrs);
  },

  // See the matching guard in `mention-node.ts`: `extendNodeSchema` is applied to every
  // node type, so an unguarded `leafText` would make every leaf in the document report
  // "Document evidence" as its text content.
  extendNodeSchema(extension) {
    if (extension.name !== "documentEvidence") return {};
    return {
      leafText: (node: PMNode) => renderDocumentEvidencePlaintext(node.attrs),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) =>
      createElement(DocumentEvidenceNodeView, {
        ...props,
      })
    );
  },

  addProseMirrorPlugins() {
    return [documentEvidenceRunsPlugin()];
  },
});
